/**
 * Main UI Manager for Proctoring SDK
 * Orchestrates all UI components with Shadow DOM encapsulation
 */

import { getStyles, applyCustomTheme } from "./styles.js";
import Notifications from "./notifications.js";
import StatusIndicator from "./statusIndicator.js";
import CameraPreview from "./cameraPreview.js";
import PermissionWizard from "./permissionWizard.js";
import ChatUI from "./chatUI.js";

// Violation type to notification mapping
const violationMessages = {
  no_face: {
    title: "Face Not Detected",
    message: "Please ensure your face is visible to the camera.",
    type: "warning",
  },
  multiple_faces: {
    title: "Multiple Faces Detected",
    message: "Only one person should be visible during the exam.",
    type: "error",
  },
  face_not_centered: {
    title: "Face Not Centered",
    message: "Please center your face in the camera frame.",
    type: "warning",
  },
  looking_away: {
    title: "Looking Away",
    message: "Please keep your eyes on the screen.",
    type: "warning",
  },
  head_rotation: {
    title: "Head Turned",
    message: "Please face the camera directly.",
    type: "warning",
  },
  cellphone_detected: {
    title: "Phone Detected",
    message: "Electronic devices are not permitted.",
    type: "error",
  },
  tab_switch: {
    title: "Tab Switch Detected",
    message: "Please stay on this tab during the exam.",
    type: "warning",
  },
  copy_paste: {
    title: "Copy/Paste Detected",
    message: "Copy/paste is not allowed during the exam.",
    type: "warning",
  },
  devtools_open: {
    title: "Developer Tools Detected",
    message: "Please close developer tools.",
    type: "error",
  },
  context_menu: {
    title: "Right-Click Detected",
    message: "Right-click is disabled during the exam.",
    type: "info",
  },
};

class ProctoringUI {
  container = null;
  shadowRoot = null;
  initialized = false;
  options = {
    enabled: true,
    position: "top-right",
    theme: "dark",
    showStatusIndicator: true,
    showCameraPreview: true,
    notifications: {
      enabled: true,
      duration: 5000,
      maxVisible: 3,
    },
  };

  init(options = {}) {
    if (this.initialized) return;

    // Merge options
    this.options = {
      ...this.options,
      ...options,
      notifications: {
        ...this.options.notifications,
        ...(options.notifications || {}),
      },
    };

    if (this.options.enabled === false) return;

    // Create Shadow DOM container
    this.container = document.createElement("div");
    this.container.id = "proctoring-sdk-ui";
    // Ensure container doesn't interfere with page layout
    this.container.style.cssText =
      "position: fixed; top: 0; left: 0; width: 0; height: 0; z-index: 999999; pointer-events: none;";
    this.shadowRoot = this.container.attachShadow({ mode: "open" });

    // Inject styles
    const styleEl = document.createElement("style");
    styleEl.textContent = getStyles(this.options.theme);

    // Apply custom theme if provided
    if (typeof this.options.theme === "object") {
      styleEl.textContent += `:host { ${applyCustomTheme(this.options.theme)} }`;
    }

    this.shadowRoot.appendChild(styleEl);

    // Initialize components
    if (this.options.notifications.enabled) {
      Notifications.init(this.shadowRoot, {
        ...this.options.notifications,
        position: this.options.position,
      });
    }

    if (this.options.showStatusIndicator) {
      StatusIndicator.init(this.shadowRoot, {
        position: this.options.position,
      });
    }

    // Initialize Permission Wizard
    PermissionWizard.init(this.shadowRoot);

    // Initialize Chat UI
    ChatUI.init(this.shadowRoot);

    if (this.options.showCameraPreview) {
      // Camera preview in opposite corner
      const previewPosition = this.options.position.includes("top")
        ? "bottom-right"
        : "top-right";
      CameraPreview.init(this.shadowRoot, {
        position: previewPosition,
      });

      // Add click handler to toggle chat
      const previewEl = this.shadowRoot.querySelector(".camera-preview");
      if (previewEl) {
        previewEl.addEventListener("click", () => {
          ChatUI.toggle();
        });
        previewEl.title = "Click to open chat";
        previewEl.style.cursor = "pointer";
      }
    }

    // Listen for unread count changes
    window.addEventListener("chat-unread-change", (e) => {
      this.updateChatBadge(e.detail.count);
    });

    document.body.appendChild(this.container);
    this.initialized = true;
  }

  /**
   * Update chat unread badge on camera preview
   */
  updateChatBadge(count) {
    if (!this.shadowRoot) return;

    const previewEl = this.shadowRoot.querySelector(".camera-preview");
    if (!previewEl) return;

    let badge = previewEl.querySelector(".chat-badge");

    if (count > 0) {
      if (!badge) {
        badge = document.createElement("div");
        badge.className = "chat-badge";
        previewEl.appendChild(badge);
      }
      badge.textContent = count > 9 ? "9+" : count;
      badge.style.display = "block";
    } else {
      if (badge) {
        badge.style.display = "none";
      }
    }
  }

  /**
   * Load chat history (call after Chat.init() has been invoked)
   */
  loadChatHistory() {
    if (!this.initialized) return;
    ChatUI.loadHistory();
  }

  /**
   * Handle incoming chat message
   * @param {Object} message
   */
  handleChatMessage(message) {
    if (!this.initialized) return;

    // Only add messages from others (invigilators)
    // Self-messages are added optimistically by ChatUI
    if (message.senderRole !== "user") {
      ChatUI.addMessage(message, false);
      this.showNotification(
        "New Message",
        `From Invigilator: ${message.message.substring(0, 30)}${message.message.length > 30 ? "..." : ""}`,
        "info",
      );
    }
  }

  /**
   * Show a notification toast
   */
  showNotification(title, message, type = "info") {
    // Auto-initialize if not already done
    if (!this.initialized) {
      this.init();
    }
    if (!this.options.notifications.enabled) return;
    return Notifications.show(title, message, type);
  }

  /**
   * Show violation alert based on type
   */
  showViolationAlert(violationType) {
    // Auto-initialize if not already done
    if (!this.initialized) {
      this.init();
    }

    const config = violationMessages[violationType] || {
      title: "Violation Detected",
      message: `Violation: ${violationType}`,
      type: "warning",
    };

    this.showNotification(config.title, config.message, config.type);
  }

  /**
   * Update status indicator
   */
  updateStatus(camera, screen) {
    if (!this.initialized || !this.options.showStatusIndicator) return;
    StatusIndicator.updateStatus(camera, screen);
  }

  /**
   * Update consumed duration shown in status indicator
   * @param {number} seconds
   */
  updateDuration(seconds) {
    if (!this.initialized || !this.options.showStatusIndicator) return;
    StatusIndicator.updateDuration(seconds);
  }

  /**
   * Start the permission wizard (step-by-step flow)
   * @param {Object} options - { userDetails, steps, callbacks }
   */
  startPermissionWizard(options = {}) {
    // Auto-initialize if not already done
    if (!this.initialized) {
      this.init();
    }
    return PermissionWizard.start(options);
  }

  /**
   * Hide the permission wizard
   */
  hidePermissionWizard() {
    PermissionWizard.hide();
  }

  /**
   * Set camera stream for preview
   */
  setCameraStream(stream) {
    if (!this.initialized || !this.options.showCameraPreview) return;
    CameraPreview.setStream(stream);
  }

  /**
   * Toggle camera preview visibility
   */
  toggleCameraPreview() {
    if (!this.initialized) return;
    CameraPreview.toggle();
  }

  /**
   * Show camera preview
   */
  showCameraPreview() {
    if (!this.initialized) return;
    CameraPreview.show();
  }

  /**
   * Hide camera preview
   */
  hideCameraPreview() {
    if (!this.initialized) return;
    CameraPreview.hide();
  }

  /**
   * Minimize status indicator
   */
  minimize() {
    if (!this.initialized) return;
    StatusIndicator.minimize();
  }

  /**
   * Expand status indicator
   */
  expand() {
    if (!this.initialized) return;
    StatusIndicator.expand();
  }

  /**
   * Show a full-screen countdown overlay
   * @param {string} title - Title of the overlay
   * @param {string} message - Message to display
   * @param {number} duration - Duration in seconds
   * @param {Function} onComplete - Callback when countdown finishes
   */
  showFullscreenCountdown(title, message, duration = 5, onComplete) {
    if (!this.initialized) this.init();

    const overlay = document.createElement("div");
    overlay.className = "fullscreen-overlay";
    overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0, 0, 0, 0.9);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            z-index: 1000000;
            color: white;
            font-family: system-ui, -apple-system, sans-serif;
            text-align: center;
        `;

    const titleEl = document.createElement("h1");
    titleEl.textContent = title;
    titleEl.style.marginBottom = "20px";
    titleEl.style.fontSize = "2.5rem";

    const messageEl = document.createElement("p");
    messageEl.textContent = message;
    messageEl.style.fontSize = "1.5rem";
    messageEl.style.marginBottom = "40px";
    messageEl.style.maxWidth = "600px";

    const timerEl = document.createElement("div");
    timerEl.style.fontSize = "4rem";
    timerEl.style.fontWeight = "bold";
    timerEl.style.color = "#ff4d4f";
    timerEl.textContent = duration;

    overlay.appendChild(titleEl);
    overlay.appendChild(messageEl);
    overlay.appendChild(timerEl);

    this.shadowRoot.appendChild(overlay);

    let timeLeft = duration;
    const interval = setInterval(() => {
      timeLeft--;
      timerEl.textContent = timeLeft;
      if (timeLeft <= 0) {
        clearInterval(interval);
        overlay.remove();
        if (onComplete) onComplete();
      }
    }, 1000);
  }

  /**
   * Destroy all UI components
   */
  destroy() {
    if (!this.initialized) return;

    Notifications.destroy();
    StatusIndicator.destroy();
    PermissionWizard.destroy();
    CameraPreview.destroy();
    ChatUI.destroy();

    this.container?.remove();
    this.container = null;
    this.shadowRoot = null;
    this.initialized = false;
  }
}

export default new ProctoringUI();
